import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, ChevronLeft, ChevronRight, Lock, Unlock, Trash2, Save, Plus, History } from 'lucide-react';

const CHANNELS = [
  { key: 'TOTAL',        label: 'Total (Blended)',  icon: null },
  { key: 'GOOGLE_ADS',   label: 'Google Ads',       icon: '/google_ads-512.webp' },
  { key: 'META_ADS',     label: 'Meta Ads',         icon: '/6033716.png' },
  { key: 'TIKTOK_ADS',   label: 'TikTok Ads',       icon: '/tik-tokiconss.png' },
  { key: 'CRITEO',       label: 'Criteo',           icon: '/CRcriteopng.png' },
  { key: 'RTB_HOUSE',    label: 'RTB House',        icon: '/RTBhouseiconPNG.png' },
  { key: 'PINTEREST',    label: 'Pinterest',        icon: '/pinterestlogopng.png' },
  { key: 'OTHER',        label: 'Other',            icon: null }
];

const REVENUE_SOURCES = ['ga4', 'shopify', 'ikas', 'panel', 'adjust', 'blended'];

// UI shows only Spend Budget + Revenue Target. Other metric columns are kept
// in the DB schema (see backend monthly_targets_v2) and remain editable via
// the API — this UI just hides them for now to keep the surface simple.
const METRIC_COLS = [
  { key: 'spend_target',       label: 'Spend Budget',     fmt: 'currency' },
  { key: 'revenue_target',     label: 'Revenue Target',   fmt: 'currency' }
];

function fmtValue(v, type) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  if (type === 'currency') return '₺' + n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
  if (type === 'decimal') return n.toFixed(2);
  return n.toLocaleString('tr-TR');
}

function parseInput(v) {
  if (v === '' || v === null || v === undefined) return null;
  let s = String(v).trim();
  if (!s) return null;
  // Keep digits, separators, minus
  s = s.replace(/[^\d.,\-]/g, '');
  if (!s) return null;

  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  // Multiple of one separator → it's the thousands separator → strip it
  if (dotCount > 1) s = s.replace(/\./g, '');
  if (commaCount > 1) s = s.replace(/,/g, '');

  // Now at most one '.' and one ',' remain
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  if (lastDot > -1 && lastComma > -1) {
    // Both single — the last one is decimal, the other is thousands
    if (lastComma > lastDot) {
      // TR style: "1.234,56" → strip dot, comma→dot
      s = s.replace('.', '').replace(',', '.');
    } else {
      // EN style: "1,234.56" → strip comma
      s = s.replace(',', '');
    }
  } else if (lastComma > -1) {
    // Only comma → treat as decimal (TR: "8,5")
    s = s.replace(',', '.');
  }
  // Only dot or nothing → already JS-parseable

  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

export default function MonthlyTargets() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [edits, setEdits] = useState({});
  const [historyOpen, setHistoryOpen] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : '';

  const fetchTargets = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://api.glowytics.com/api/monthly-targets?year=${year}&month=${month}`,
        { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
      );
      if (!res.ok) throw new Error('Failed to load monthly targets');
      const data = await res.json();
      // Backend wraps the array in { data: [...] }. Older callers sent the
      // bare array, so accept both shapes.
      const rows = Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) ? data.data : []);
      setTargets(rows);
      setEdits({});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, year, month]);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  // Build a map channel_key → target row (only TOTAL/ORGANIC/DIRECT + one account per paid channel)
  const byChannel = useMemo(() => {
    const m = {};
    for (const t of targets) {
      const key = t.channel_key;
      // Prefer account_id=null (default channel-level) over account-specific
      if (!m[key] || (!t.account_id && m[key].account_id)) m[key] = t;
    }
    return m;
  }, [targets]);

  // Live sum of all non-TOTAL channels (paid + organic + direct), respecting
  // any unsaved edits in the form. Used to show "Σ channels = ₺X" under the
  // Total row and detect mismatches between Total and the sum of breakdowns.
  const channelSum = useMemo(() => {
    const out = { spend_target: 0, revenue_target: 0 };
    for (const ch of CHANNELS) {
      if (ch.key === 'TOTAL') continue;
      for (const m of METRIC_COLS) {
        const editKey = `${ch.key}::${m.key}`;
        let v;
        if (editKey in edits) v = edits[editKey];
        else v = byChannel[ch.key]?.[m.key];
        const n = parseInput(v);
        if (Number.isFinite(n)) out[m.key] += n;
      }
    }
    return out;
  }, [byChannel, edits]);

  const changeMonth = (delta) => {
    let y = year;
    let m = month + delta;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y);
    setMonth(m);
  };

  const getEditedValue = (channel_key, metric) => {
    const editKey = `${channel_key}::${metric}`;
    if (editKey in edits) return edits[editKey];
    const row = byChannel[channel_key];
    return row ? (row[metric] ?? '') : '';
  };

  const setEdit = (channel_key, metric, val) => {
    setEdits(prev => ({ ...prev, [`${channel_key}::${metric}`]: val }));
  };

  const saveRow = async (channel_key) => {
    if (!brandId) return;
    const row = byChannel[channel_key] || {};
    const payload = {
      year,
      month,
      channel_key,
      account_id: row.account_id || null,
      revenue_source: row.revenue_source || 'ga4'
    };
    let hasChange = false;
    for (const m of METRIC_COLS) {
      const editKey = `${channel_key}::${m.key}`;
      if (editKey in edits) {
        payload[m.key] = parseInput(edits[editKey]);
        hasChange = true;
      }
    }
    if (!hasChange) {
      toast('No changes', { icon: 'ℹ️' });
      return;
    }
    setSaving(prev => ({ ...prev, [channel_key]: true }));
    try {
      const res = await fetch('https://api.glowytics.com/api/monthly-targets', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Save failed');
      toast.success(`${CHANNELS.find(c => c.key === channel_key)?.label || channel_key} target saved`);
      // Clear edits for this channel and refresh
      setEdits(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => {
          if (k.startsWith(`${channel_key}::`)) delete next[k];
        });
        return next;
      });
      fetchTargets();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(prev => ({ ...prev, [channel_key]: false }));
    }
  };

  const toggleLock = async (channel_key) => {
    const row = byChannel[channel_key];
    if (!row?.id) return;
    const path = row.is_locked ? 'unlock' : 'lock';
    try {
      const res = await fetch(`https://api.glowytics.com/api/monthly-targets/${row.id}/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Toggle lock failed');
      toast.success(row.is_locked ? 'Unlocked' : 'Locked');
      fetchTargets();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteRow = async (channel_key) => {
    const row = byChannel[channel_key];
    if (!row?.id) return;
    if (!window.confirm(`Delete all targets for ${channel_key}?`)) return;
    try {
      const res = await fetch(`https://api.glowytics.com/api/monthly-targets/${row.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Delete failed');
      }
      toast.success('Deleted');
      fetchTargets();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openHistory = async (channel_key) => {
    const row = byChannel[channel_key];
    if (!row?.id) return;
    setHistoryOpen(channel_key);
    try {
      const res = await fetch(`https://api.glowytics.com/api/monthly-targets/${row.id}/history`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      const rows = Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) ? data.data : []);
      setHistoryData(rows);
    } catch (e) {
      setHistoryData([]);
    }
  };

  const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  if (!brandId) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
        Please select a brand first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Monthly Targets</h3>
          <p className="text-sm text-gray-500 mt-1">
            Set monthly spend budgets, revenue goals, and conversion targets per channel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeMonth(-1)}
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
            disabled={loading}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium min-w-[160px] text-center">
            {monthName}
          </div>
          <button
            onClick={() => changeMonth(1)}
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
            disabled={loading}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-8 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">Channel</th>
                  {METRIC_COLS.map(m => (
                    <th key={m.key} className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{m.label}</th>
                  ))}
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {CHANNELS.map(ch => {
                  const row = byChannel[ch.key];
                  const isLocked = row?.is_locked;
                  const hasEdits = Object.keys(edits).some(k => k.startsWith(`${ch.key}::`));
                  const isTotal = ch.key === 'TOTAL';
                  return (
                    <tr key={ch.key} className={isLocked ? 'bg-gray-50/40' : (isTotal ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'hover:bg-blue-50/20')}>
                      <td className={`px-3 py-2 sticky left-0 ${isTotal ? 'bg-blue-50/40' : 'bg-white'}`}>
                        <div className="flex items-center gap-2">
                          {ch.icon && <img src={ch.icon} alt={ch.label} className="w-4 h-4 object-contain" />}
                          <span className={`text-xs ${isTotal ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>{ch.label}</span>
                        </div>
                      </td>
                      {METRIC_COLS.map(m => {
                        const val = getEditedValue(ch.key, m.key);
                        const displayVal = val === '' || val === null ? '' : val;
                        const numericVal = parseInput(val);
                        const sumForCol = channelSum[m.key] || 0;
                        const totalIsEmpty = isTotal && (val === '' || val === null || !Number.isFinite(numericVal));
                        const totalHasValue = isTotal && Number.isFinite(numericVal);
                        const showSumHelper = isTotal && sumForCol > 0;
                        const delta = totalHasValue ? (numericVal - sumForCol) : 0;
                        return (
                          <td key={m.key} className="px-2 py-1.5 text-right">
                            <div className="flex flex-col items-end gap-0.5">
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={displayVal}
                                  onChange={(e) => setEdit(ch.key, m.key, e.target.value)}
                                  disabled={isLocked}
                                  placeholder={isTotal && sumForCol > 0 ? fmtValue(sumForCol, m.fmt) : '—'}
                                  className={`w-32 px-2 py-1 text-right text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                    isLocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : (isTotal ? 'bg-white border-blue-200 focus:border-blue-500 font-semibold' : 'bg-white border-gray-200 focus:border-blue-400')
                                  } ${(`${ch.key}::${m.key}` in edits) ? 'border-amber-400 bg-amber-50' : ''}`}
                                />
                                {totalIsEmpty && sumForCol > 0 && !isLocked && (
                                  <button
                                    type="button"
                                    onClick={() => setEdit(ch.key, m.key, String(sumForCol))}
                                    className="px-1.5 py-1 text-[10px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
                                    title={`Apply sum (${fmtValue(sumForCol, m.fmt)}) to Total`}
                                  >
                                    Σ
                                  </button>
                                )}
                              </div>
                              {!isTotal && val !== '' && val !== null && !isNaN(Number(val)) && (
                                <div className="text-[10px] text-gray-400">{fmtValue(val, m.fmt)}</div>
                              )}
                              {showSumHelper && (
                                <div className="text-[10px] text-gray-500">
                                  Σ channels = <span className="font-mono">{fmtValue(sumForCol, m.fmt)}</span>
                                  {totalHasValue && delta !== 0 && (
                                    <span className={`ml-1 px-1 rounded ${
                                      Math.abs(delta) < sumForCol * 0.01 ? 'text-gray-400'
                                      : delta > 0 ? 'text-emerald-600 bg-emerald-50'
                                      : 'text-red-600 bg-red-50'
                                    }`}>
                                      {delta > 0 ? `+${fmtValue(delta, m.fmt)} unallocated` : `${fmtValue(Math.abs(delta), m.fmt)} over`}
                                    </span>
                                  )}
                                  {totalHasValue && delta === 0 && (
                                    <span className="ml-1 text-emerald-600">✓</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => saveRow(ch.key)}
                            disabled={!hasEdits || saving[ch.key] || isLocked}
                            className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            {saving[ch.key] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                          {row?.id && (
                            <>
                              <button
                                onClick={() => toggleLock(ch.key)}
                                className={`p-1.5 rounded-md ${isLocked ? 'text-amber-600 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                title={isLocked ? 'Unlock' : 'Lock'}
                              >
                                {isLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => openHistory(ch.key)}
                                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100"
                                title="History"
                              >
                                <History className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteRow(ch.key)}
                                disabled={isLocked}
                                className="p-1.5 rounded-md text-red-500 hover:bg-red-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 space-y-1">
            <div>💡 Leave fields empty to skip. Amber border = unsaved changes. Lock a row to prevent accidental changes.</div>
            <div>📊 <strong>Total (Blended)</strong> can be entered directly or auto-summed from channel rows. Use the <span className="inline-block px-1 bg-blue-50 border border-blue-200 rounded font-mono">Σ</span> button to apply the channel sum to Total. Mismatch (Total ≠ Σ) shown as <span className="text-emerald-600">unallocated</span> or <span className="text-red-600">over</span>.</div>
          </div>
        </div>
      )}

      {/* History modal */}
      {historyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setHistoryOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Target History — {CHANNELS.find(c => c.key === historyOpen)?.label}</h3>
              <button onClick={() => setHistoryOpen(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyData.length === 0 ? (
                <p className="text-sm text-gray-500 italic text-center py-8">No history entries.</p>
              ) : (
                <div className="space-y-2">
                  {historyData.map(h => (
                    <div key={h.id} className="p-3 border border-gray-200 rounded-lg text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                          h.change_type === 'insert' ? 'bg-green-100 text-green-700' :
                          h.change_type === 'delete' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {h.change_type}
                        </span>
                        <span className="text-gray-500">{new Date(h.changed_at).toLocaleString('tr-TR')}</span>
                      </div>
                      <pre className="text-[11px] bg-gray-50 p-2 rounded overflow-x-auto">{JSON.stringify(h.snapshot, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
